import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import { BdFeaturePermissionService } from '@bindoc-projects/bindoc-permissions-lib/data';
import { BdFeature } from '@bindoc-projects/feature-catalog-domain-lib';
import {
  BdAuthenticationService,
  BdFeatureStatusService,
} from '@bindoc-projects/security-lib/data';
import { BdMenuHandler } from './menu-handler';
import { BdMenuLoadingComponent } from './menu-loading.component';
import { MenuItem } from 'primeng/api';
import { Badge } from 'primeng/badge';
import { Ripple } from 'primeng/ripple';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { PanelMenuModule } from 'primeng/panelmenu';
import { Tooltip } from 'primeng/tooltip';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'ays-menu',
  template: `
    <!--
      One anchor shape for every menu entry — the pinned Home and Klia rows above
      the accordion, and the grouped entries inside it. Keeping them on one
      template is what stops an entry kind from silently going missing: the Klia
      row used to render only when it had a routerLink, so a user without
      KLIA:BASE (whose item BdMenuHandler deliberately turns into a url
      cross-sell) got no Klia entry at all, while every other locked tool showed
      one.

      Branch order is deliberate: a disabled item wins, so a maintenance-flagged
      or locked feature renders its warning/lock icon on a dead anchor instead of
      a live link into a route that would bounce.
    -->
    <ng-template #menuLink let-item>
      @if (item.disabled) {
        <a pRipple class="flex items-center px-4 py-2 app-menu-empty">
          <ng-container
            [ngTemplateOutlet]="menuLinkBody"
            [ngTemplateOutletContext]="{ $implicit: item }"
          />
        </a>
      } @else if (item.routerLink) {
        <a
          [routerLink]="item.routerLink"
          [routerLinkActive]="'bg-(--p-content-hover-background) font-bold'"
          [routerLinkActiveOptions]="
            item.routerLinkActiveOptions ?? { exact: false }
          "
          pRipple
          class="flex items-center px-4 py-2"
        >
          <ng-container
            [ngTemplateOutlet]="menuLinkBody"
            [ngTemplateOutletContext]="{ $implicit: item }"
          />
        </a>
      } @else if (item.url) {
        <a
          [href]="item.url"
          [attr.target]="item.target ?? null"
          [attr.rel]="item.target === '_blank' ? 'noopener noreferrer' : null"
          [pTooltip]="
            item.styleClass === 'app-menu-cross-sell'
              ? 'Nicht in Ihrem Plan enthalten — mehr erfahren'
              : null
          "
          tooltipPosition="right"
          pRipple
          [class]="
            'flex items-center px-4 py-2 ' +
            (item.styleClass === 'app-menu-cross-sell'
              ? 'app-menu-cross-sell text-(--p-text-muted-color) hover:bg-(--p-content-hover-background)'
              : 'app-menu-url')
          "
        >
          <ng-container
            [ngTemplateOutlet]="menuLinkBody"
            [ngTemplateOutletContext]="{ $implicit: item }"
          />
        </a>
      } @else {
        <a pRipple class="flex items-center px-4 py-2 app-menu-empty">
          <ng-container
            [ngTemplateOutlet]="menuLinkBody"
            [ngTemplateOutletContext]="{ $implicit: item }"
          />
        </a>
      }
    </ng-template>

    <!-- The anchor's contents, identical in every branch. -->
    <ng-template #menuLinkBody let-item>
      <span class="ml-2 flex-1">{{ item.label }}</span>
      @if (item.icon) {
        <span [class]="item.icon"></span>
      }
      @if (item.badge) {
        <p-badge class="ml-auto" [value]="item.badge" />
      }
      @if (item.shortcut) {
        <span
          class="ml-auto border border-surface rounded bg-emphasis text-muted-color text-xs p-1"
          >{{ item.shortcut }}</span
        >
      }
      @if (item.items) {
        <i
          class="pi text-primary ml-auto"
          [class.pi-angle-down]="!item.expanded"
          [class.pi-angle-up]="item.expanded"
        ></i>
      }
    </ng-template>

    <!--
      items() stays undefined until BdMenuHandler first emits — which needs MSAL
      and a live feature-permission fetch, and never happens during SSR. Show
      placeholder rows until then instead of an empty sidebar (ZD #4539).
    -->
    @if (items() === undefined) {
      <bd-menu-loading />
    } @else {
      @let hi = homeItem();
      @if (hi && hi.visible !== false) {
        <ng-container
          [ngTemplateOutlet]="menuLink"
          [ngTemplateOutletContext]="{ $implicit: hi }"
        />
        @for (child of hi.items ?? []; track child.label) {
          @if (child.visible !== false) {
            <ng-container
              [ngTemplateOutlet]="menuLink"
              [ngTemplateOutletContext]="{ $implicit: child }"
            />
          }
        }
      }
      @let ki = kliaItem();
      @if (ki && ki.visible !== false) {
        <ng-container
          [ngTemplateOutlet]="menuLink"
          [ngTemplateOutletContext]="{ $implicit: ki }"
        />
      }
      <p-panelmenu
        [model]="items()"
        [style]="{ border: 'none' }"
        [multiple]="true"
      >
        <ng-template #item let-item>
          <ng-container
            [ngTemplateOutlet]="menuLink"
            [ngTemplateOutletContext]="{ $implicit: item }"
          />
        </ng-template>
      </p-panelmenu>
    }
  `,
  imports: [
    Badge,
    BdMenuLoadingComponent,
    NgTemplateOutlet,
    Ripple,
    RouterLink,
    PanelMenuModule,
    RouterLinkActive,
    Tooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AysMenuComponent implements OnInit {
  readonly features = input<BdFeature<string>[]>();

  public readonly items = signal<MenuItem[] | undefined>(undefined);
  public readonly homeItem = signal<MenuItem | null>(null);
  public readonly kliaItem = signal<MenuItem | null>(null);

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _router = inject(Router);
  private readonly _authenticationService = inject(BdAuthenticationService);
  private readonly _featurePermissionService = inject(
    BdFeaturePermissionService,
  );
  private readonly _featureStatusService = inject(BdFeatureStatusService);
  private menuHandler: BdMenuHandler;

  constructor() {
    effect(() => {
      const features = this.features();
      if (features) {
        this.initMenuHandler();
      }
    });
  }

  ngOnInit(): void {
    if (!this._isBrowser) return;
    this.subscribeToRouteChanges();
  }

  private initMenuHandler(): void {
    this.menuHandler = new BdMenuHandler(
      this.features(),
      this._authenticationService.authentication$,
      this._authenticationService.msalInitialized,
      this._featureStatusService,
      this._featurePermissionService,
      this._destroyRef,
    );

    this.menuHandler.homeItem$.subscribe((homeItem) => {
      this.homeItem.set(homeItem);
    });

    this.menuHandler.kliaItem$.subscribe((kliaItem) => {
      this.kliaItem.set(kliaItem);
    });

    this.menuHandler.menu$.subscribe((menu) => {
      this.items.set(menu);
      this.expandActiveMenuItem(this._router.url);
    });
  }

  private subscribeToRouteChanges(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((event) => {
        const navEnd = event as NavigationEnd;
        this.expandActiveMenuItem(navEnd.urlAfterRedirects || navEnd.url);
      });
  }

  /**
   * Expands only the parent menu item(s) that contain the active route.
   * All other items are collapsed.
   * @param currentUrl The current URL to match against menu items
   */
  private expandActiveMenuItem(currentUrl: string): void {
    const currentItems = this.items();
    if (!currentItems) {
      return;
    }

    // Normalize the URL (remove query params and fragments)
    const normalizedUrl = currentUrl.split('?')[0].split('#')[0];

    // Collapse all items first, then expand only the matching path
    currentItems.forEach((item) => {
      this.collapseAll(item);
    });

    // Expand only the items that contain the active route
    currentItems.forEach((item) => {
      this.expandIfContainsActiveRoute(item, normalizedUrl);
    });
  }

  /**
   * Recursively collapses all menu items.
   * @param item The menu item to collapse
   */
  private collapseAll(item: MenuItem): void {
    if (item.items && item.items.length > 0) {
      item.expanded = false;
      item.items.forEach((child) => this.collapseAll(child));
    }
  }

  /**
   * Recursively checks if a menu item or its children match the current route.
   * If a match is found, expands the parent item.
   * @param item The menu item to check
   * @param currentUrl The current URL
   * @returns true if this item or any of its children match the current route
   */
  private expandIfContainsActiveRoute(
    item: MenuItem,
    currentUrl: string,
  ): boolean {
    // Check if this item's routerLink matches the current URL
    const itemMatches = this.routerLinkMatchesUrl(item.routerLink, currentUrl);

    // Check if any child items match
    let childMatches = false;
    if (item.items && item.items.length > 0) {
      for (const child of item.items) {
        if (this.expandIfContainsActiveRoute(child, currentUrl)) {
          childMatches = true;
        }
      }
    }

    // If this item or any child matches, expand this item (if it has children)
    if ((itemMatches || childMatches) && item.items && item.items.length > 0) {
      item.expanded = true;
    }

    return itemMatches || childMatches;
  }

  /**
   * Checks if a routerLink matches the current URL.
   * @param routerLink The routerLink (can be string, string[], or undefined)
   * @param currentUrl The current URL
   * @returns true if the routerLink matches the current URL
   */
  private routerLinkMatchesUrl(
    routerLink: string | string[] | undefined,
    currentUrl: string,
  ): boolean {
    if (!routerLink) {
      return false;
    }

    // Convert routerLink to a path string
    const linkPath = Array.isArray(routerLink)
      ? '/' + routerLink.join('/')
      : routerLink.startsWith('/')
        ? routerLink
        : '/' + routerLink;

    // Normalize both paths
    const normalizedLink = linkPath.replace(/\/+/g, '/');
    const normalizedUrl = currentUrl.replace(/\/+/g, '/');

    // Check if URL starts with the link path (to handle child routes)
    return (
      normalizedUrl === normalizedLink ||
      normalizedUrl.startsWith(normalizedLink + '/')
    );
  }
}
